import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { Baby, GraduationCap, Briefcase, Heart, Sparkles, Brain } from "lucide-react";

// Life stages for the "growing up" story (fast version - 1s per stage)
const LIFE_STAGES = [
  { 
    key: "birth", 
    icon: Baby, 
    en: "Being born...", 
    de: "Wird geboren...",
    detail_en: "A new persona enters the world",
    detail_de: "Eine neue Persona erblickt das Licht der Welt"
  },
  { 
    key: "childhood", 
    icon: Sparkles, 
    en: "Growing up...", 
    de: "Wächst auf...",
    detail_en: "Developing personality",
    detail_de: "Entwickelt Persönlichkeit"
  },
  { 
    key: "education", 
    icon: GraduationCap, 
    en: "Getting educated...", 
    de: "Wird ausgebildet...",
    detail_en: "Learning skills",
    detail_de: "Erlernt Fähigkeiten"
  },
  { 
    key: "career", 
    icon: Briefcase, 
    en: "Building career...", 
    de: "Baut Karriere auf...",
    detail_en: "Finding their path",
    detail_de: "Findet den Lebensweg"
  },
  { 
    key: "present", 
    icon: Heart, 
    en: "Living their life...", 
    de: "Lebt ihr Leben...",
    detail_en: "Ready to share",
    detail_de: "Bereit zu teilen"
  },
];

export const PersonaSkeleton = ({ index, totalCount }) => {
  const { language } = useLanguage();
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Stagger animation start
  const staggerDelay = index * 300;

  useEffect(() => {
    const startTimer = setTimeout(() => {
      // Progress animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return 95;
          return prev + (Math.random() * 3 + 1);
        });
      }, 150);

      // Stage progression - 3 seconds per stage for 15 second total
      const stageInterval = setInterval(() => {
        setCurrentStage(prev => {
          if (prev >= LIFE_STAGES.length - 1) return LIFE_STAGES.length - 1;
          return prev + 1;
        });
      }, 3000); // 3 seconds per stage = 15 seconds total

      return () => {
        clearInterval(progressInterval);
        clearInterval(stageInterval);
      };
    }, staggerDelay);

    return () => clearTimeout(startTimer);
  }, [staggerDelay]);

  const stage = LIFE_STAGES[currentStage];
  const StageIcon = stage.icon;
  const stageText = language === 'de' ? stage.de : stage.en;
  const stageDetail = language === 'de' ? stage.detail_de : stage.detail_en;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="relative overflow-hidden rounded-2xl bg-zinc-900/50 border border-white/5 p-5"
    >
      {/* Subtle pulsing background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-amber-500/10"
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      <div className="relative z-10">
        {/* Avatar with life stage icon */}
        <div className="relative w-20 h-20 mx-auto mb-4">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-yellow-500/30"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center">
            <motion.div
              key={currentStage}
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StageIcon className="w-8 h-8 text-yellow-400" />
            </motion.div>
          </div>
        </div>

        {/* Persona number & stage */}
        <div className="text-center mb-4">
          <div className="text-xs text-zinc-500 mb-1">Persona #{index + 1}</div>
          
          <motion.div
            key={currentStage}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-semibold text-white text-sm"
          >
            {stageText}
          </motion.div>
          
          <div className="text-xs text-zinc-400 mt-1">{stageDetail}</div>
        </div>

        {/* Life stage progress dots */}
        <div className="flex justify-center gap-2 mb-4">
          {LIFE_STAGES.map((s, i) => (
            <div
              key={s.key}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                i <= currentStage ? 'bg-yellow-400' : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>

        {/* Skeleton lines */}
        <div className="space-y-2 mb-4">
          {[0.6, 0.8, 0.5].map((width, i) => (
            <div key={i} className="h-2 bg-zinc-800 rounded overflow-hidden">
              <motion.div
                className="h-full bg-yellow-400/30"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                style={{ width: `${width * 100}%` }}
              />
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-xs text-yellow-300/80">
                {language === 'de' ? 'KI generiert...' : 'AI generating...'}
              </span>
            </div>
            <span className="text-xs text-zinc-400 font-mono">{Math.round(progress)}%</span>
          </div>
          
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-yellow-500 to-amber-500"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PersonaSkeleton;
